import {
    ActivityTrackerUtils,
    DocumentVisibilityTracker,
    InteractionElement,
    InteractionSection,
    InteractionTargetVerbose,
    MutationEvent,
    MutationType,
    PageEvent,
    PulseEnvironment,
} from '@pulse/browser-shared';
import { TrackerConfigurations, isToggleOn } from '@pulse/common';
import { Observable, ReplaySubject, from, fromEvent } from 'rxjs';
import { filter, map, mergeAll, tap } from 'rxjs/operators';
import { TabManagerService } from '../tabs';

export const WINDOW_INTERACTION_EVENTS: Array<keyof WindowEventMap> = [
    'click',
    'contextmenu',
    // To be used later if needed, as we don't fire events based on them now

    // 'mousedown',
    // 'mouseup',
    // 'touchstart',
    // 'touchend',
    // 'dragstart',
    // 'dragend',
    // 'dblclick',
    // 'drop',
    // 'keydown',
    // 'keyup',
    // 'keypress',
    // 'input',
    // 'submit',
    // 'reset'
];

export const WINDOW_EVENTS: Array<keyof WindowEventMap> = [
    ...WINDOW_INTERACTION_EVENTS,
    // To be used later if needed, as we don't fire events based on them now
    // 'focus',
    // 'blur',
    // 'focusin',
    // 'focusout',
    // 'change'
];

export class ActivityTracker {
    private interactionEventsSubject?: ReplaySubject<PageEvent>;
    private mutationEventsSubject?: ReplaySubject<MutationEvent>;
    lastClickSourceInformation?: InteractionSection;

    constructor(
        private visibilityTracker: DocumentVisibilityTracker,
        private win: PulseEnvironment,
        private tabManagerService: TabManagerService,
        private config: TrackerConfigurations,
        private activityTrackerUtils: ActivityTrackerUtils,
    ) {
        this.registerListeners();
    }

    interactions(): Observable<PageEvent> {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.interactionEventsSubject!;
    }

    mutations(): Observable<MutationEvent> {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.mutationEventsSubject!;
    }

    private registerListeners() {
        if (!this.interactionEventsSubject) {
            const interactionEvents = this.interactionEvents();
            const mutationEvents = this.mutationEvents();

            const interactionEventsEmitter = new ReplaySubject<PageEvent>();
            const mutationEventsEmitter = new ReplaySubject<MutationEvent>();
            interactionEvents.subscribe(interactionEventsEmitter);
            mutationEvents.subscribe(mutationEventsEmitter);

            this.interactionEventsSubject = interactionEventsEmitter;
            this.mutationEventsSubject = mutationEventsEmitter;
        }
    }

    private mutationEvents(): Observable<MutationEvent> {
        const emitter = new ReplaySubject<any>();
        const observer = new MutationObserver((mutations) => {
            const addedElements = this.getMutatedElements(mutations, 'addedNodes');
            const removedElements = this.getMutatedElements(mutations, 'removedNodes');

            if (!addedElements.length && !removedElements.length) {
                return;
            }

            emitter.next({ mutatedElements: [...addedElements, ...removedElements] });
        });

        observer.observe(document, { childList: true, subtree: true });

        return emitter;
    }

    private shouldCollectDynamicAttributes(componentInfo: {
        component: string;
        componentType?: string;
    }): boolean {
        return Boolean(componentInfo.component && componentInfo.componentType);
    }

    private isHidden(el: HTMLElement) {
        const style = this.win.win.getComputedStyle(el);

        return (
            style.display === 'none' || style.visibility === 'hidden' || style.display === undefined
        );
    }

    private interactionEvents(): Observable<PageEvent> {
        const body = this.win.doc.body;
        const listenerOptions: AddEventListenerOptions = {
            capture: true,
            passive: true,
        };

        return from(WINDOW_EVENTS).pipe(
            map((eventType) => fromEvent(body, eventType, listenerOptions)),
            mergeAll(),
            map((e) => this.processWindowEvent(e)),
            filter(Boolean),
            tap((event) => {
                if (
                    (event.type !== 'click' && event.type !== 'contextmenu') ||
                    event.doNotTrack ||
                    !event.clickable
                ) {
                    return;
                }

                this.lastClickSourceInformation = {
                    name: event.section.name,
                    group: event.section.group,
                    element: event.element,
                    popupComponent: event.section.popupComponent,
                    popupComponentType: event.section.popupComponentType,
                    url: event.docURL,
                    tab_id: isToggleOn(this.config.eventEnrichment, 'tab_id')
                        ? this.tabManagerService.getTabId()
                        : '',
                    additionalProperties: event.additionalProperties,
                };
            }),
        );
    }

    private getMutatedElements(mutations: MutationRecord[], type: MutationType) {
        return mutations
            .flatMap((mutation) => {
                const target = mutation.target;
                const mutatedNodes = Array.from(mutation[type]);

                return mutatedNodes.map((node) => ({ node, target }));
            })
            .filter(({ node }) => node instanceof HTMLElement)
            .flatMap((_) => {
                const target = this.getInteractionInfoFor(_.node, _.target);
                let element: InteractionElement;

                if (!target) {
                    return [];
                }

                if (
                    !target ||
                    (!this.activityTrackerUtils.hasAttribute('pulse-component', target.element) &&
                        !this.activityTrackerUtils.hasAttribute(
                            'data-pulse-component',
                            target.element,
                        ))
                ) {
                    const nestedElements = target.element.querySelectorAll(
                        '[pulse-component], [data-pulse-component]',
                    );

                    if (nestedElements && nestedElements[0]) {
                        element = this.activityTrackerUtils.createInteractionElement(
                            nestedElements[0] as HTMLElement,
                        );
                    } else {
                        return [];
                    }
                } else {
                    element = this.activityTrackerUtils.createInteractionElement(target.element);
                }

                const component = (element.attributeValueByName['pulse-component'] ||
                    element.attributeValueByName['data-pulse-component']) as string;

                const componentType =
                    element.attributeValueByName['pulse-component-type'] ||
                    element.attributeValueByName['data-pulse-component-type'];

                if (type === 'addedNodes' && this.isHidden(target.element)) {
                    return [];
                }

                const additionalProperties = this.shouldCollectDynamicAttributes({
                    component,
                    componentType,
                })
                    ? this.activityTrackerUtils.collectDynamicAttributes(target.element)
                    : undefined;

                return {
                    element,
                    component,
                    componentType: componentType || component,
                    mutationType: type,
                    section: this.activityTrackerUtils.createSection(
                        target.section,
                        target.sectionGroup,
                    ),
                    additionalProperties:
                        additionalProperties && Object.keys(additionalProperties).length > 0
                            ? additionalProperties
                            : undefined,
                };
            });
    }

    private processWindowEvent(event: Event): PageEvent | null {
        if (!event.target) {
            return null;
        }

        const interactionInfo = this.getInteractionInfoFor(event.target);

        if (!interactionInfo) {
            return null;
        }

        const dynamicAttributes = this.activityTrackerUtils.collectDynamicAttributes(
            interactionInfo.element,
        );
        const doc = this.win.doc;

        return {
            type: event.type,
            trusted: event.isTrusted ?? true,
            eventPhase: event.eventPhase,
            bubbles: event.bubbles,
            section: this.activityTrackerUtils.createSection(
                interactionInfo.section,
                interactionInfo.sectionGroup,
                interactionInfo.popupComponent,
                Object.keys(dynamicAttributes).length > 0 ? dynamicAttributes : undefined,
            ),
            element: this.activityTrackerUtils.createInteractionElement(interactionInfo.element),

            docFocused: doc.hasFocus(),
            docVisible: this.visibilityTracker.visible,
            docURL: doc.location.href,
            doNotTrack: interactionInfo.doNotTrack,
            clickable: interactionInfo.clickable,
            ts: Date.now(),
            additionalProperties:
                Object.keys(dynamicAttributes).length > 0 ? dynamicAttributes : undefined,
        };
    }

    private getInteractionInfoFor(
        node: EventTarget,
        target?: Node,
    ): InteractionTargetVerbose | undefined {
        const doc = this.win.doc;
        const interactionNode: Node =
            !(node instanceof Node) || node === doc || node === doc.head
                ? doc.documentElement
                : node;

        let interactionElement = this.activityTrackerUtils.getFirstParentElement(interactionNode);

        if (!interactionElement) {
            return;
        }

        const elementType = this.activityTrackerUtils.getTargetType(interactionNode);
        const targetClosestSection =
            target instanceof Element
                ? target.closest<HTMLElement>('[pulse-section]') ||
                  target.closest<HTMLElement>('[data-pulse-section]')
                : undefined;

        const targetClosestGroup =
            target instanceof Element
                ? target.closest<HTMLElement>('[pulse-section-group]') ||
                  target.closest<HTMLElement>('[data-pulse-section-group]')
                : undefined;

        const sectionElement =
            targetClosestSection ||
            interactionElement.closest<HTMLElement>('[pulse-section]') ||
            interactionElement.closest<HTMLElement>('[data-pulse-section]') ||
            undefined;

        const popupComponent =
            interactionElement.closest<HTMLElement>('[pulse-component]') ||
            interactionElement.closest<HTMLElement>('[data-pulse-component]') ||
            undefined;

        const sectionGroup =
            targetClosestGroup ||
            interactionElement.closest<HTMLElement>('[pulse-section-group]') ||
            interactionElement.closest<HTMLElement>('[data-pulse-section-group]') ||
            undefined;

        const clickableElement = this.activityTrackerUtils.clickableTarget(interactionElement);

        /*
         * If event trackable set target element which market as clickable
         * In case of parent element of clicked element is trackable (button, link or with pulse-clickable attribute) override target
         * */
        interactionElement = clickableElement || interactionElement;

        // Do not track if any of parent elements have pulse-dnt attribute
        const doNotTrack =
            Boolean(interactionElement.closest<HTMLElement>('[pulse-dnt]')) ||
            Boolean(interactionElement.closest<HTMLElement>('[data-pulse-dnt]'));

        return {
            clickable: Boolean(clickableElement),
            doNotTrack: Boolean(doNotTrack),
            type: elementType,
            element: interactionElement,
            section: sectionElement,
            sectionGroup,
            popupComponent,
        };
    }
}
